import { ApolloClient, ApolloLink, from, HttpLink, InMemoryCache, Observable } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import * as Sentry from '@sentry/browser'
import moment from 'moment'
import config from './config'
import {
  setToken,
  setUid,
  setClient,
  clearUserData,
  getExpiry,
  setExpiry,
  getToken,
  getUid,
  getClient,
  setTimer
} from './utils/helper'
let abortController = new AbortController()
export const resetAbortController = (reason = 'Request cancelled') => {
  abortController.abort(reason)
  abortController = new AbortController()
}
const getTimeLeft = () => moment.unix(getExpiry()).diff(moment())
const httpLink = new HttpLink({
  uri: config.apollo.networkInterface,
  fetch: (uri, options) => {
    return fetch(uri, {
      ...options,
      signal: abortController.signal
    })
  }
})

// Function to validate the token
const validateToken = async () => {
  if (!getExpiry()) {
    clearUserData()
    window.location.replace('/login')
    return false
  }
  if (getTimeLeft() < 0) {
    clearUserData()
    window.location.replace('/login')
    return false
  }
  if (getTimeLeft() < 20000) {
    const response = await fetch(`${config.apollo.baseUrl}/auth/validate_token`, {
      method: 'GET',
      headers: {
        'Content-type': 'application/json',
        'access-token': getToken(),
        'token-type': 'Bearer',
        uid: getUid(),
        client: getClient(),
        expiry: getExpiry()
      }
    })

    if (!response.ok) {
      clearUserData()
      window.location.replace('/login')
      return false
    }

    const data = await response.json()
    if (data.success) {
      return true
    }
    clearUserData()
    window.location.replace('/login')
    return false
  }
  return true
}

// Middleware link to pause requests and validate the token
const authMiddleware = new ApolloLink(
  (operation, forward) =>
    new Observable(observer => {
      validateToken()
        .then(isValid => {
          if (isValid) {
            forward(operation).subscribe({
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer)
            })
          } else {
            observer.error(new Error('Invalid token'))
          }
        })
        .catch(error => {
          observer.error(error)
        })
    })
)

// Setup the header for the request
const middlewareAuthLink = new ApolloLink((operation, forward) => {
  const client = getClient()
  if (!client) {
    clearUserData()
  }

  operation.setContext({
    headers: {
      'Content-type': 'application/json',
      'access-token': getToken(),
      'token-type': 'Bearer',
      uid: getUid(),
      client
    }
  })

  return forward(operation)
})

// Replaces apollo-link-sentry's SentryLink (removed: incompatible with Sentry v8)
const sentryLink = new ApolloLink(
  (operation, forward) =>
    new Observable(observer => {
      forward(operation).subscribe({
        next: result => {
          if (result.errors?.length) {
            result.errors.forEach(error => {
              Sentry.addBreadcrumb({
                type: 'error',
                category: 'graphql',
                message: error.message,
                data: {
                  operationName: operation.operationName
                },
                level: 'error'
              })
            })
          }
          observer.next(result)
        },
        error: error => {
          Sentry.addBreadcrumb({
            type: 'error',
            category: 'graphql',
            message: error.message ?? 'Network error',
            data: {
              operationName: operation.operationName
            },
            level: 'error'
          })
          observer.error(error)
        },
        complete: observer.complete.bind(observer)
      })
    })
)

// After the backend responds, we take the refreshToken from headers if it exists, and save it in the localStorage.
const afterwareLink = new ApolloLink((operation, forward) =>
  forward(operation).map(response => {
    const context = operation.getContext()
    const {
      response: { headers }
    } = context

    if (headers) {
      const newToken = headers.get('access-token')
      if (newToken?.length) setToken(newToken)
      const newUid = headers.get('uid')
      if (newUid?.length) setUid(newUid)
      const newClient = headers.get('client')
      if (newClient?.length) {
        setClient(newClient)
      }
      const newExpiry = headers.get('expiry')
      if (newExpiry?.length) {
        console.debug('Setting new expiry: ', newExpiry)
        setExpiry(newExpiry)
      } else {
        console.debug('NOT setting new expiry: ', newExpiry)
      }
    }

    return response
  })
)

const errorLink = onError(({ operation, graphQLErrors, networkError, response }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      if (message === 'access denied' && getTimeLeft() - 10000 < 10000) {
        setTimer(
          JSON.stringify({
            last_expiry: moment.unix(getExpiry()).toLocaleString(),
            date: moment().toLocaleString(),
            message: 'access denied'
          })
        )
        Sentry.withScope(scope => {
          scope.setExtras({
            user: getUid(),
            response: JSON.stringify(response),
            graphQLErrors: JSON.stringify(graphQLErrors),
            networkError: JSON.stringify(networkError),
            operation: JSON.stringify(operation),
            timeLeft: getTimeLeft(),
            message,
            path
          })
          const transactionId = operation
            .getContext()
            ?.request?.http?.headers?.get('x-transaction-id')
          if (transactionId) scope.setTransaction(transactionId)

          // NOTE - CK: Integrate it again if its still a problem
          // Sentry.captureException(new Error('access denied WITHOUT time left'))
        })
        clearUserData()
        window.location.replace('/login')
      }
      if (message === 'access denied' && getTimeLeft() - 10000 > 10000) {
        Sentry.withScope(scope => {
          scope.setExtras({
            user: getUid(),
            response: JSON.stringify(response),
            graphQLErrors: JSON.stringify(graphQLErrors),
            networkError: JSON.stringify(networkError),
            operation: JSON.stringify(operation),
            timeLeft: getTimeLeft(),
            message,
            path
          })
          const transactionId = operation
            .getContext()
            ?.request?.http?.headers?.get('x-transaction-id')
          if (transactionId) scope.setTransaction(transactionId)

          Sentry.captureException(new Error('access denied WITH time left'))
        })
        clearUserData()
        window.location.replace('/login')
      }
      if (message === 'forbidden' && getTimeLeft() - 10000 > 10000) {
        window.location.replace('/cases')
      }
      console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
    })
  }

  if (networkError) console.warn(`[Network error]: ${networkError}`)
})

const cache = new InMemoryCache()

const client = new ApolloClient({
  link: from([sentryLink, authMiddleware, middlewareAuthLink, afterwareLink, errorLink, httpLink]),
  cache
})

export default client
